import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiCloseLine, RiFileList3Line, RiArrowRightLine, RiFlightTakeoffFill, RiFlightLandFill,
  RiCalendarEventLine, RiTimeLine, RiPlaneFill, RiTeamLine,
  RiCheckLine, RiWindyLine, RiSunLine, RiFlagLine, RiFileTextLine,
  RiArrowRightSLine, RiLiveLine, RiMapPinLine,
} from '@remixicon/react';
import { Drawer, DrawerContent, DrawerClose } from '@components/ui/Drawer';
import StatusBadge from './StatusBadge';
import { formatUTCToDateTime } from '@/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { getAircraftModelName } from '@/features/tails/utils';
import { useAirportByIcao } from '@/features/quotes/services/airportService';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS } from '@/lib/permissions';
import { apiClient } from '@/lib/api';
import { QuoteDetailsDrawer } from '@/features/quotes/components';
import AdvanceInvoiceDrawer from '@/features/quotes/components/AdvanceInvoiceDrawer';

const SCORE_COLOR = (n) => {
  if (n >= 80) return '#4ade80';
  if (n >= 60) return '#facc15';
  if (n >= 40) return '#fb923c';
  return '#f87171';
};

const ScoreRing = ({ value, label }) => {
  const radius = 35;
  const strokeWidth = 4;
  const nr = radius - strokeWidth / 2;
  const circumference = nr * 2 * Math.PI;
  const numeric = typeof value === 'number' && Number.isFinite(value);
  const pct = numeric ? Math.max(0, Math.min(100, value)) : 0;
  const color = SCORE_COLOR(pct);
  return (
    <div className="flex flex-col items-center justify-center gap-1">
      <div className="relative w-12 h-12">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={nr} fill="transparent" stroke="#e5e7eb" strokeWidth={strokeWidth} />
          {numeric && (
            <circle cx="50" cy="50" r={nr} fill="transparent" stroke={color} strokeWidth={strokeWidth}
              strokeDasharray={circumference} strokeDashoffset={circumference - (pct / 100) * circumference}
              strokeLinecap="round" className="transition-all duration-500 ease-out" />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className={`text-xs font-semibold leading-none ${numeric ? 'text-gray-800' : 'text-gray-400'}`}>
            {numeric ? Math.round(pct) : '—'}
          </span>
          <span className="text-[8px] text-gray-500 leading-none">{label}</span>
        </div>
      </div>
    </div>
  );
};

const FlightDetailDrawer = ({ isOpen, onClose, flight }) => {

  const navigate = useNavigate();
  const { can } = usePermissions();

  // Two exact ICAO lookups instead of the old full-list fetch. Hooks must
  // run before any early return, so we compute the codes here up top with
  // optional-chaining fallbacks (undefined-safe on missing flight).
  const depIcaoForLookup = flight?.route?.from || flight?.departure_airport;
  const arrIcaoForLookup = flight?.route?.to || flight?.arrival_airport;
  const { data: depAirportInfo } = useAirportByIcao(depIcaoForLookup);
  const { data: arrAirportInfo } = useAirportByIcao(arrIcaoForLookup);

  const quoteId = flight?.quoteId || flight?.quote_id || flight?.quote?.quote_id || flight?.quote?.id;

  const { data: linkedInvoices = [] } = useQuery({
    queryKey: ['invoices', 'quote', quoteId],
    queryFn: () => apiClient.getInvoices({ quote_id: quoteId, include: 'items,payments' }),
    enabled: !!quoteId && isOpen,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const [isQuoteDrawerOpen, setIsQuoteDrawerOpen] = useState(false);
  const [invoiceDrawerData, setInvoiceDrawerData] = useState(null);
  const [invoiceDrawerId, setInvoiceDrawerId] = useState(null);
  const [invoiceDrawerStatus, setInvoiceDrawerStatus] = useState(null);
  const [isInvoiceDrawerOpen, setIsInvoiceDrawerOpen] = useState(false);

  if (!flight) return null;

  const flightId = flight.flight_id || flight.id;
  const clientId = flight.clientId || flight.quote?.client?.client_id;
  const clientEmail = flight.clientEmail || flight.quote?.client?.email;
  const quoteNumber = flight.quoteNumber;
  const tailNumber = flight.tailNumber || flight.aircraft?.tail_number || 'TBD';
  const flightStatus = (flight.status || flight.flight_status || 'PENDING').toUpperCase();
  const clientName = flight.clientName
    || [flight.quote?.client?.first_name, flight.quote?.client?.last_name].filter(Boolean).join(' ')
    || 'Client';
  const depIcao = flight.route?.from || flight.departure_airport;
  const arrIcao = flight.route?.to || flight.arrival_airport;
  const departureDate = flight.departure_date
    || (flight.departureDate instanceof Date ? flight.departureDate.toISOString() : null);
  const aircraftType = getAircraftModelName(flight.aircraft);
  const passengerCount = flight.quote?.acceptedOption?.passenger_count
    || flight.quote?.passenger_count
    || '-';
  const legs = flight.quote?.bid_items?.[0]?.bid?.leg_itinerary || [];

  const rawTrust = flight?.aircraft?.operator?.trust_score;
  const trustScore = (rawTrust == null || rawTrust === '' || Number.isNaN(Number(rawTrust)))
    ? null : Number(rawTrust);

  const canReportAog = can(MODULES.TRACKING, ACTIONS.UPDATE);
  const canViewTripSheet = can(MODULES.BOOKING, ACTIONS.CREATE)
    && can(MODULES.BOOKING, ACTIONS.UPDATE)
    && can(MODULES.BOOKING, ACTIONS.DELETE);
  const depTime = departureDate ? new Date(departureDate) : null;
  const showTripSheetButton = quoteId && depTime && depTime > new Date();

  const fmtDate = (dateStr, icao) =>
    dateStr ? formatUTCToDateTime(dateStr, getAirportTimezone(icao), 'MMM D') : '—';
  const fmtTime = (dateStr, icao) =>
    dateStr ? formatUTCToDateTime(dateStr, getAirportTimezone(icao), 'HH:mm') : '—';

  const depAirportLabel = depAirportInfo?.name
    ? `${depAirportInfo.name} (${depIcao})`
    : depIcao ? `${depIcao} Airport` : '—';

  const tripCity = arrAirportInfo?.city || depAirportInfo?.city || '';
  const routeLabel = depIcao && arrIcao ? `${depIcao} - ${arrIcao}` : '';
  const tripLabel = tripCity && routeLabel
    ? `${tripCity} Trip (${routeLabel})`
    : routeLabel;

  const quotePaymentStatus = (flight.quote?.payment_status || '').toUpperCase();
  const isQuotePaid = quotePaymentStatus === 'CAPTURED' || quotePaymentStatus === 'PAID';

  const tripType = legs.length > 1 ? 'Round Trip' : 'One Way';
  const depWeather = { label: 'Windy', icon: RiWindyLine };
  const arrWeather = { label: 'Sunny', icon: RiSunLine };

  const displayLegs = legs.length > 0
    ? legs
    : depIcao && arrIcao
      ? [{ departure_airport: depIcao, arrival_airport: arrIcao, departure_date: departureDate }]
      : [];

  return (
    <>
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent side="right" hideCloseButton className="w-full max-w-md lg:max-w-2xl p-0 flex flex-col">
        {/* Header */}
        <div className="px-5 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold text-gray-900">{tailNumber}</h2>
              <p className="text-sm text-gray-500 mt-1">Flight for {clientName} {flight?.quote?.quote_number || "test"}</p>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`/additional-services-invoice`, {
                      state: {
                        tripLabel: quoteNumber ? `${quoteNumber} (${depIcao} - ${arrIcao})` : `${depIcao} - ${arrIcao}`,
                        clientId,
                        clientName,
                        clientEmail,
                        quoteId,
                        flightId,
                        quoteNumber,
                      },
                    });
                  }}
                  className="p-1 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors"
                  title="Additional services invoice"
                >
                  <RiFileList3Line className="w-4 h-4" />
                </button>
                <DrawerClose asChild>
                  <button className="p-1 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors">
                    <RiCloseLine className="w-4 h-4" />
                  </button>
                </DrawerClose>
              </div>
              <StatusBadge status={flightStatus} />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 space-y-4 pb-4">
          {/* Trip Details */}
          <div className="rounded-3xl border border-gray-100 overflow-hidden p-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 px-4 pt-3 pb-0">
              Trip Details
            </p>

            {/* Passenger count + trip type */}
            <div className="grid grid-cols-2 gap-4 px-4 pt-3 pb-4">
              <div className="flex items-start gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                  <RiTeamLine className="h-4 w-4 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 mb-1">Passengers</p>
                  <p className="text-base font-semibold text-gray-900">{passengerCount}</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                  <RiMapPinLine className="h-4 w-4 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 mb-1">{tripType}</p>
                  <p className="text-base font-semibold text-gray-900">{depIcao} → {arrIcao}</p>
                </div>
              </div>
            </div>

            {/* Departure date */}
            <div className="flex items-start gap-2.5 px-4 pb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                <RiCalendarEventLine className="h-4 w-4 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 mb-1">Departure Date</p>
                <p className="text-base font-semibold text-gray-900">
                  {departureDate
                    ? formatUTCToDateTime(departureDate, getAirportTimezone(depIcao), 'dddd, MMMM D, YYYY')
                    : '—'}
                </p>
              </div>
            </div>

            {/* Legs */}
            <div className="border-t border-gray-100">
              {displayLegs.map((leg, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm ${i < displayLegs.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex items-center gap-1.5 font-medium text-gray-900">
                    <span className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs">
                      <RiFlightTakeoffFill className="w-3 h-3" />
                      {leg.departure_airport}
                    </span>
                    <RiArrowRightLine className="w-3.5 h-3.5 text-gray-400" />
                    <span className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs">
                      <RiFlightLandFill className="w-3 h-3" />
                      {leg.arrival_airport}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <RiCalendarEventLine className="w-3 h-3" />
                      {fmtDate(leg.departure_date, leg.departure_airport)}
                    </span>
                    <span className="flex items-center gap-1">
                      <RiTimeLine className="w-3 h-3" />
                      {fmtTime(leg.departure_date, leg.departure_airport)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Aircraft / airport / passengers */}
            <div className="px-4 py-3 space-y-2 text-sm text-gray-500">
              {aircraftType && (
                <div className="flex items-center gap-2">
                  <RiPlaneFill className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>{aircraftType}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <RiMapPinLine className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate">{depAirportLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <RiTeamLine className="w-4 h-4 text-gray-400 shrink-0" />
                <span>{passengerCount} passengers</span>
              </div>
            </div>

            {/* Status chips */}
            <div className="px-4 py-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 py-1 px-2.5 rounded-full">
                <RiCheckLine className="w-3.5 h-3.5" /> CLIENT PAID
              </span>
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 py-1 px-2.5 rounded-full">
                <RiCheckLine className="w-3.5 h-3.5" /> CONTRACT SIGNED
              </span>
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 py-1 px-2.5 rounded-full">
                <RiCheckLine className="w-3.5 h-3.5" /> N O AOG
              </span>
            </div>

            {/* Weather + scores */}
            <div className="px-4 py-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <depWeather.icon className="w-4 h-4 text-gray-400" />
                      {depWeather.label}
                    </span>
                    <span className="text-gray-400">{depIcao}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <arrWeather.icon className="w-4 h-4 text-gray-400" />
                      {arrWeather.label}
                    </span>
                    <span className="text-gray-400">{arrIcao}</span>
                  </div>
                </div>
                <div className="rounded-2xl bg-green-50 flex items-center justify-around py-2">
                  <ScoreRing value={trustScore} label="Trust" />
                  <ScoreRing value={null} label="Trip" />
                </div>
              </div>
            </div>

            {/* View Live Flight Tracking */}
            <div className="px-4 py-3">
              <button
                type="button"
                onClick={() => { onClose(); navigate(`/track/${flightId}`); }}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <RiLiveLine className="w-4 h-4" />
                View Live Flight Tracking
              </button>
            </div>
          </div>

          {/* Linked Invoices */}
          {quoteId && (
            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 px-4 pt-3 pb-2">
                Linked Invoices
              </p>
              <div className="flex flex-col gap-2 p-3 pt-0">
                {/* Trip Quote row — primary quote payment */}
                <button
                  type="button"
                  onClick={() => setIsQuoteDrawerOpen(true)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">Trip Quote</span>
                      {isQuotePaid ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                          <RiCheckLine className="w-3 h-3" /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                          Pending Payment
                        </span>
                      )}
                    </div>
                    {tripLabel && (
                      <p className="text-xs text-gray-500 mt-0.5">{tripLabel}</p>
                    )}
                  </div>
                  <RiArrowRightSLine className="w-4 h-4 text-gray-400 shrink-0" />
                </button>

                {/* Additional / advance invoice rows */}
                {linkedInvoices.map((inv) => {
                  const invoicePaid = (inv.status || '').toUpperCase() === 'PAID';
                  const typeLabel = inv.invoice_type === 'ADDITIONAL'
                    ? 'Additional Service Invoice'
                    : inv.invoice_type === 'ADVANCE'
                      ? 'Advance Invoice'
                      : (inv.invoice_number || 'Invoice');
                  const itemCount = inv.items?.length || 0;
                  const subtitle = itemCount > 0
                    ? `${itemCount} service${itemCount === 1 ? '' : 's'} listed`
                    : '';
                  return (
                    <button
                      key={inv.invoice_id}
                      type="button"
                      onClick={() => {
                        setInvoiceDrawerData(inv);
                        setInvoiceDrawerId(inv.invoice_id);
                        setInvoiceDrawerStatus(inv.status);
                        setIsInvoiceDrawerOpen(true);
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{typeLabel}</span>
                          {invoicePaid ? (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                              <RiCheckLine className="w-3 h-3" /> Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                              Pending Payment
                            </span>
                          )}
                        </div>
                        {subtitle && (
                          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
                        )}
                      </div>
                      <RiArrowRightSLine className="w-4 h-4 text-gray-400 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(canReportAog || (canViewTripSheet && showTripSheetButton)) && (
          <div className="px-5 pb-6 pt-3 border-t border-gray-100 flex items-center gap-3">
            {canReportAog && (
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-full bg-red-50 border border-red-200 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors"
              >
                <RiFlagLine className="w-4 h-4" />
                Report AOG
              </button>
            )}
            {canViewTripSheet && showTripSheetButton && (
              <button
                type="button"
                onClick={() => { onClose(); navigate(`/trip-sheet?quoteid=${quoteId}&flight=true`); }}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-full border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <RiFileTextLine className="w-4 h-4" />
                View Trip Sheet
              </button>
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>

    <QuoteDetailsDrawer
      isOpen={isQuoteDrawerOpen}
      onOpenChange={setIsQuoteDrawerOpen}
      quoteId={quoteId}
    />

    <AdvanceInvoiceDrawer
      isOpen={isInvoiceDrawerOpen}
      onClose={() => setIsInvoiceDrawerOpen(false)}
      invoice={invoiceDrawerData}
      invoiceId={invoiceDrawerId}
      status={invoiceDrawerStatus}
    />
    </>
  );
};

export default FlightDetailDrawer;
